/**
 * Partner Integration Service
 * 
 * Manages partner integration configurations:
 * - Email Notifications
 * - API Access Keys
 * - Webhooks
 */

import pb from '../lib/pocketbase';
import {
  PartnerEmailNotification,
  PartnerApiKey,
  PartnerWebhook,
  WebhookDeliveryLog,
  IntegrationActivityLog
} from '../types';

// =====================================================
// Email Notification Service
// =====================================================

export class EmailNotificationService {
  private static collection = 'partner_email_notifications';

  /**
   * Get email notification configuration for a partner
   */
  static async getConfiguration(partnerId: string): Promise<PartnerEmailNotification | null> {
    try {
      const records = await pb.collection(this.collection).getFullList({
        filter: `partner_id = "${partnerId}"`
      });
      
      if (records.length === 0) return null;
      
      const record = records[0];
      return {
        ...record,
        recipient_emails: JSON.parse(record.recipient_emails || '[]')
      } as PartnerEmailNotification;
    } catch (error) {
      console.error('Failed to get email notification config:', error);
      return null;
    }
  }

  /**
   * Create or update email notification configuration
   */
  static async saveConfiguration(
    partnerId: string,
    config: Omit<PartnerEmailNotification, 'id' | 'partner_id' | 'created_at' | 'updated_at'>
  ): Promise<PartnerEmailNotification | null> {
    try {
      // Check if configuration exists
      const existing = await this.getConfiguration(partnerId);
      
      const data = {
        partner_id: partnerId,
        ...config,
        recipient_emails: JSON.stringify(config.recipient_emails),
        updated_at: new Date().toISOString()
      };

      let record;
      if (existing) {
        // Update existing
        record = await pb.collection(this.collection).update(existing.id, data);
      } else {
        // Create new
        record = await pb.collection(this.collection).create(data);
      }

      return {
        ...record,
        recipient_emails: JSON.parse(record.recipient_emails)
      } as PartnerEmailNotification;
    } catch (error) {
      console.error('Failed to save email notification config:', error);
      return null;
    }
  }

  /**
   * Delete email notification configuration
   */
  static async deleteConfiguration(partnerId: string): Promise<boolean> {
    try {
      const existing = await this.getConfiguration(partnerId);
      if (!existing) return false;
      
      await pb.collection(this.collection).delete(existing.id);
      return true;
    } catch (error) {
      console.error('Failed to delete email notification config:', error);
      return false;
    }
  }
}

// =====================================================
// API Key Service
// =====================================================

export class ApiKeyService {
  private static collection = 'partner_api_keys';

  /**
   * Generate a cryptographically secure random API key
   */
  static generateApiKey(): string {
    const prefix = 'pk_live_';
    const array = new Uint8Array(24);
    crypto.getRandomValues(array);
    const randomPart = Array.from(array, byte => 
      byte.toString(16).padStart(2, '0')
    ).join('');
    return prefix + randomPart;
  }

  /**
   * Get all API keys for a partner
   */
  static async getKeys(partnerId: string): Promise<PartnerApiKey[]> {
    try {
      const records = await pb.collection(this.collection).getFullList({
        filter: `partner_id = "${partnerId}"`,
        sort: '-created'
      });
      
      return records as PartnerApiKey[];
    } catch (error) {
      console.error('Failed to get API keys:', error);
      return [];
    }
  }

  /**
   * Create a new API key
   */
  static async createKey(
    partnerId: string,
    config: Partial<Omit<PartnerApiKey, 'id' | 'partner_id' | 'api_key' | 'created_at' | 'updated_at'>>
  ): Promise<PartnerApiKey | null> {
    try {
      const apiKey = this.generateApiKey();
      
      const data = {
        partner_id: partnerId,
        api_key: apiKey,
        api_key_name: config.api_key_name || 'Default',
        read_campaigns: config.read_campaigns ?? true,
        write_campaigns: config.write_campaigns ?? false,
        read_advertisements: config.read_advertisements ?? true,
        write_advertisements: config.write_advertisements ?? false,
        read_promo_requests: config.read_promo_requests ?? true,
        write_promo_requests: config.write_promo_requests ?? false,
        read_analytics: config.read_analytics ?? true,
        rate_limit_per_hour: config.rate_limit_per_hour ?? 1000,
        rate_limit_per_day: config.rate_limit_per_day ?? 10000,
        enabled: config.enabled ?? true,
        expires_at: config.expires_at || null
      };

      const record = await pb.collection(this.collection).create(data);
      return record as PartnerApiKey;
    } catch (error) {
      console.error('Failed to create API key:', error);
      return null;
    }
  }

  /**
   * Update an API key
   */
  static async updateKey(
    keyId: string,
    updates: Partial<Omit<PartnerApiKey, 'id' | 'partner_id' | 'api_key' | 'created_at'>>
  ): Promise<PartnerApiKey | null> {
    try {
      const data = {
        ...updates,
        updated_at: new Date().toISOString()
      };

      const record = await pb.collection(this.collection).update(keyId, data);
      return record as PartnerApiKey;
    } catch (error) {
      console.error('Failed to update API key:', error);
      return null;
    }
  }

  /**
   * Delete an API key
   */
  static async deleteKey(keyId: string): Promise<boolean> {
    try {
      await pb.collection(this.collection).delete(keyId);
      return true;
    } catch (error) {
      console.error('Failed to delete API key:', error);
      return false;
    }
  }

  /**
   * Regenerate an API key (creates new key, deletes old one)
   */
  static async regenerateKey(keyId: string): Promise<PartnerApiKey | null> {
    try {
      const oldKey = await pb.collection(this.collection).getOne(keyId);
      const newApiKey = this.generateApiKey();
      
      const record = await pb.collection(this.collection).update(keyId, {
        api_key: newApiKey,
        updated_at: new Date().toISOString()
      });
      
      return record as PartnerApiKey;
    } catch (error) {
      console.error('Failed to regenerate API key:', error);
      return null;
    }
  }
}

// =====================================================
// Webhook Service
// =====================================================

export class WebhookService {
  private static collection = 'partner_webhooks';
  private static logsCollection = 'webhook_delivery_logs';

  /**
   * Generate a cryptographically secure webhook secret key
   */
  static generateSecretKey(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const randomPart = Array.from(array, byte => 
      byte.toString(16).padStart(2, '0')
    ).join('');
    return 'whsec_' + randomPart;
  }

  /**
   * Generate HMAC-SHA256 signature for webhook payload
   * @param payload - The JSON payload to sign
   * @param secret - The webhook secret key
   * @returns The HMAC signature as hex string
   */
  static async generateHmacSignature(payload: string, secret: string): Promise<string> {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);
    const messageData = encoder.encode(payload);
    
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    
    const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);
    return Array.from(new Uint8Array(signature))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Get all webhooks for a partner
   */
  static async getWebhooks(partnerId: string): Promise<PartnerWebhook[]> {
    try {
      const records = await pb.collection(this.collection).getFullList({
        filter: `partner_id = "${partnerId}"`,
        sort: '-created'
      });
      
      return records as PartnerWebhook[];
    } catch (error) {
      console.error('Failed to get webhooks:', error);
      return [];
    }
  }

  /**
   * Create a new webhook
   */
  static async createWebhook(
    partnerId: string,
    config: Partial<Omit<PartnerWebhook, 'id' | 'partner_id' | 'created_at' | 'updated_at'>>
  ): Promise<PartnerWebhook | null> {
    try {
      console.log('🔧 Creating webhook for partner:', partnerId);
      const secretKey = this.generateSecretKey();
      
      const data = {
        partner_id: partnerId,
        webhook_url: config.webhook_url!,
        webhook_name: config.webhook_name || 'Default Webhook',
        
        // Event subscriptions (JSON array)
        event_subscriptions: config.event_subscriptions || [],
        
        // Security
        secret_key: secretKey,
        auth_header: config.auth_header || null,
        
        // Retry settings
        max_retries: config.max_retries ?? 3,
        retry_delay_seconds: config.retry_delay_seconds ?? 60,
        timeout_seconds: config.timeout_seconds ?? 30,
        
        // Status
        enabled: config.enabled ?? true,
        failure_count: 0
      };

      const record = await pb.collection(this.collection).create(data);
      return record as PartnerWebhook;
    } catch (error) {
      console.error('Failed to create webhook:', error);
      return null;
    }
  }

  /**
   * Update a webhook
   */
  static async updateWebhook(
    webhookId: string,
    updates: Partial<Omit<PartnerWebhook, 'id' | 'partner_id' | 'created_at'>>
  ): Promise<PartnerWebhook | null> {
    try {
      const data = {
        ...updates,
        updated_at: new Date().toISOString()
      };

      const record = await pb.collection(this.collection).update(webhookId, data);
      return record as PartnerWebhook;
    } catch (error) {
      console.error('Failed to update webhook:', error);
      return null;
    }
  }

  /**
   * Delete a webhook
   */
  static async deleteWebhook(webhookId: string): Promise<boolean> {
    try {
      await pb.collection(this.collection).delete(webhookId);
      return true;
    } catch (error) {
      console.error('Failed to delete webhook:', error);
      return false;
    }
  }

  /**
   * Get webhook delivery logs
   */
  static async getDeliveryLogs(
    webhookId: string,
    limit: number = 50
  ): Promise<WebhookDeliveryLog[]> {
    try {
      const records = await pb.collection(this.logsCollection).getList(1, limit, {
        filter: `webhook_id = "${webhookId}"`,
        sort: '-created'
      });
      
      return records.items.map(record => ({
        ...record,
        event_data: record.event_data ? JSON.parse(record.event_data) : null
      })) as WebhookDeliveryLog[];
    } catch (error) {
      console.error('Failed to get webhook delivery logs:', error);
      return [];
    }
  }

  /**
   * Test webhook endpoint (sends a test payload)
   */
  static async testWebhook(webhookId: string): Promise<{ success: boolean; message: string }> {
    try {
      const webhook = await pb.collection(this.collection).getOne(webhookId);
      
      const testPayload = {
        event: 'webhook.test',
        timestamp: new Date().toISOString(),
        data: {
          message: 'This is a test webhook from Click & Claim'
        }
      };

      const payloadString = JSON.stringify(testPayload);
      const timestamp = Math.floor(Date.now() / 1000).toString();
      const signaturePayload = `${timestamp}.${payloadString}`;
      const signature = await this.generateHmacSignature(signaturePayload, webhook.secret_key || '');
      
      const response = await fetch(webhook.webhook_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Webhook-Signature': `t=${timestamp},v1=${signature}`,
          'X-Webhook-Timestamp': timestamp,
          ...(webhook.auth_header ? { 'Authorization': webhook.auth_header } : {})
        },
        body: payloadString
      });

      const success = response.ok;
      
      // Log the test
      await pb.collection(this.logsCollection).create({
        webhook_id: webhookId,
        partner_id: webhook.partner_id,
        event_type: 'webhook.test',
        event_data: JSON.stringify(testPayload),
        http_status: response.status,
        response_body: await response.text(),
        success,
        attempt_number: 1
      });

      return {
        success,
        message: success ? 'Webhook test successful!' : `Webhook test failed with status ${response.status}`
      };
    } catch (error: any) {
      console.error('Failed to test webhook:', error);
      
      // Better error handling for network errors
      let errorMessage = 'Failed to test webhook';
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = 'Network error: Could not reach webhook URL. Check CORS settings or URL validity.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        message: errorMessage
      };
    }
  }
}

// =====================================================
// Integration Activity Logger
// =====================================================

export class IntegrationActivityLogger {
  private static collection = 'integration_activity_logs';

  static async log(
    partnerId: string,
    integrationType: 'email_notifications' | 'api_access' | 'webhook_access',
    action: 'created' | 'updated' | 'deleted' | 'enabled' | 'disabled',
    userId?: string,
    userName?: string,
    changes?: Record<string, any>
  ): Promise<void> {
    try {
      await pb.collection(this.collection).create({
        partner_id: partnerId,
        integration_type: integrationType,
        action,
        user_id: userId || null,
        user_name: userName || null,
        changes: changes ? JSON.stringify(changes) : null
      });
    } catch (error) {
      console.error('Failed to log integration activity:', error);
    }
  }

  static async getActivityLogs(
    partnerId: string,
    limit: number = 50
  ): Promise<IntegrationActivityLog[]> {
    try {
      const records = await pb.collection(this.collection).getList(1, limit, {
        filter: `partner_id = "${partnerId}"`,
        sort: '-created'
      });
      
      return records.items.map(record => ({
        ...record,
        changes: record.changes ? JSON.parse(record.changes) : null
      })) as IntegrationActivityLog[];
    } catch (error) {
      console.error('Failed to get activity logs:', error);
      return [];
    }
  }
}
